/**
 * "Has a long-running job finished *since we started watching*?"
 *
 * Both places that react to a validation run settling — the query hook that
 * refreshes the worklist, and the tab that toasts the outcome — need the same
 * judgement, and both need it to be exactly right:
 *
 * * a run that had **already finished before we arrived** is history, not news.
 *   Reacting to it would refetch the whole batch surface on every mount and
 *   toast an outcome the user saw days ago;
 * * a run that finishes **while we are watching** is the whole point.
 *
 * The trap is the batch that has *nothing* terminal when we arrive — never
 * validated, or a run still going. Baselining only once something terminal shows
 * up makes the first run to finish look like pre-existing history, so it gets
 * swallowed: precisely the case where the worklist most needs refreshing and the
 * user most needs telling. So the baseline is taken on the first observation
 * whether or not anything is terminal, `null` included.
 *
 * `key` scopes the baseline to a subject (a batch id). Watching a different one
 * re-baselines rather than comparing run ids across two batches.
 *
 * `terminal` of `undefined` means "we have not heard yet" — no response, or one
 * still in flight — which is NOT the same as "nothing has finished". Baselining
 * on it would let the first status response after a mount race decide history,
 * so it is ignored entirely and the baseline waits for a real answer.
 */

export type SettledBaseline = {
  key: string;
  /** The finished run we have already accounted for; `null` = none had. */
  runId: string | null;
};

export function observeSettled(
  prior: SettledBaseline | null,
  key: string,
  terminal: string | null | undefined,
): { baseline: SettledBaseline | null; isNew: boolean } {
  if (terminal === undefined) {
    return { baseline: prior && prior.key === key ? prior : null, isNew: false };
  }
  if (!prior || prior.key !== key) {
    return { baseline: { key, runId: terminal }, isNew: false };
  }
  if (terminal === null || prior.runId === terminal) {
    return { baseline: prior, isNew: false };
  }
  return { baseline: { key, runId: terminal }, isNew: true };
}
